/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { ScheduleDeltaEntry, ScheduleValidation } from '@src/app/model/schedule-delta';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { MessageService } from '@src/app/shared/message.service';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';

/** Shows what a schedule's apportionment would become if it were saved again, grouped by what
 * happens to each unit. Read only - the user has to save the schedule to apply any of it.
 */
@Component({
    selector: 'app-schedule-validation',
    imports: [MatDialogModule, MatButtonModule, MatIconModule, DialogBannerComponent, RouterLink],
    templateUrl: './schedule-validation.component.html',
    styleUrl: './schedule-validation.component.scss'
})
export class ScheduleValidationComponent {
    dialogRef = inject(MatDialogRef<ScheduleValidationComponent>);
    data: ScheduleValidation = inject(MAT_DIALOG_DATA);
    scheduleSvc = inject(ScheduleService);
    msgSvc = inject(MessageService);

    applying = false;

    title = $localize`Validate ${this.data.scheduleName}`;

    adds = this.data.delta.adds;
    deletes = this.data.delta.deletes;
    updates = this.data.delta.updates;

    /** One line at the top, so the size of the job is clear without scrolling the lists. */
    summary = $localize`${this.adds.length} to add, ${this.deletes.length} to remove, `
        + $localize`${this.updates.length} to update`;

    /** A portion left behind by a deleted unit has no name to show, so fall back to the id. */
    unitName(entry: ScheduleDeltaEntry) {
        return entry.unitName ?? entry.unitId;
    }

    /** A real routerLink rather than a click, so the unit can be opened in a new tab to check
     * what changed on it. A unit that no longer exists has nothing to link to.
     */
    unitUrl(entry: ScheduleDeltaEntry) {
        return entry.unitName ? UnitPageNavRoute.getIdUrl(entry.unitId) : null;
    }

    /** Type and category together - it is the pair that decides which schedule a unit lands on,
     * so they read better as one column than two.
     */
    unitType(entry: ScheduleDeltaEntry) {
        if (!entry.typeName) {
            return '';
        }
        return entry.categoryName ? `${entry.typeName} / ${entry.categoryName}` : entry.typeName;
    }

    /** Closes with the refreshed schedule, which reloads the page onto the applied portions.
     * Closing any other way returns nothing, so the page is left alone.
     */
    applyChanges() {
        this.applying = true;
        this.scheduleSvc.applySchedulePortions(this.data.scheduleId, this.data.revision)
            .subscribe(schedule => {
                if (schedule) {
                    this.msgSvc.show($localize`Schedule portions successfully synchronised`);
                    this.dialogRef.close(schedule);
                } else {
                    this.applying = false;
                }
            });
    }

    /** Percentages are held to seven places - show them as a percentage rather than a fraction. */
    asPercent(value: number) {
        return (value * 100).toFixed(5) + '%';
    }

    /* Changing one unit moves that unit's portion but ripples the percentage across every unit on
     * the schedule. Highlighting only what actually moved makes the cause stand out from the
     * ripple. Tolerances match the server's, so nothing is bolded that it did not count as a
     * change - decimal(9,3) on portion and decimal(10,7) on percent.
     */
    portionChanged(entry: ScheduleDeltaEntry) {
        return Math.abs(entry.oldPortion - entry.portion) > 0.0005;
    }

    percentChanged(entry: ScheduleDeltaEntry) {
        return Math.abs(entry.oldPercent - entry.percent) > 0.00000015;
    }
}
